import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiRequestError, apiDownload } from '../api/client.ts';
import i18n from '../i18n/index.ts';

/**
 * Downloading a PDF, with something on screen while it happens.
 *
 * Every PDF in this app is built on demand — the quote render goes through
 * Browser Rendering, and a receipt with no stored copy is generated by the
 * request that asks for it — so seconds pass between the click and the file.
 * As plain `<a href>` links those seconds were invisible: nothing spins,
 * nothing changes, and the reasonable read is that the click missed. Clicking
 * again then costs a second render against the same quota.
 *
 * So downloads run through `fetch` here instead. Two indicators come out of
 * one call: the button that was pressed goes busy (its own local state, so
 * only that row spins), and a page-level status is published to the store
 * below, which `PdfDownloadIndicator` renders wherever the app is mounted —
 * including for downloads started from a control that scrolls out of view or
 * unmounts mid-flight.
 *
 * The store is module-level rather than a context so any component can start a
 * download without a provider in its tree — which is also what lets the tests
 * for a single row render that row alone.
 */

export interface PdfDownloadStatus {
  /** How many downloads are in flight; a count, since two can overlap. */
  active: number;
  /** The last failure, already localized, until it is dismissed. */
  error: string | null;
}

let status: PdfDownloadStatus = { active: 0, error: null };
const listeners = new Set<() => void>();

function publish(next: PdfDownloadStatus): void {
  status = next;
  for (const listener of listeners) listener();
}

/**
 * The one rule both of these follow: a failure is only ever discarded once it
 * has actually been on screen.
 *
 * Downloads overlap — a receipt list is a column of them — and the indicator
 * shows the spinner rather than the alert whenever anything is still running.
 * So `active === 0` is exactly the condition under which a stored error is
 * visible, and the only state from which discarding one is honest. Anywhere
 * else, dropping it would retire a failure nobody was ever shown, leaving the
 * click that produced no file saying nothing at all — the silence this whole
 * hook exists to remove.
 */

/** Starts one download, clearing an error the reader has by now seen. */
function beginDownload(): void {
  publish({ active: status.active + 1, error: status.active === 0 ? null : status.error });
}

/** Ends one, keeping any failure still waiting for its turn on screen. */
function finishDownload(error: string | null): void {
  publish({ active: Math.max(0, status.active - 1), error: error ?? status.error });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): PdfDownloadStatus {
  return status;
}

/** Subscribes to the page-level download status. */
export function usePdfDownloadStatus(): PdfDownloadStatus {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

export function dismissPdfDownloadError(): void {
  if (status.error === null) return;
  publish({ ...status, error: null });
}

/** Test seam: drops any in-flight count and error left over from a test. */
export function resetPdfDownloadStatus(): void {
  publish({ active: 0, error: null });
}

/**
 * Hands the fetched bytes to the browser as a download.
 *
 * The object URL is revoked on a timer rather than immediately: Safari reads it
 * asynchronously after the synthetic click, and revoking in the same tick
 * leaves it with nothing to save.
 */
function saveBlob(blob: Blob, filename: string): void {
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(href), 60_000);
}

function describeFailure(error: unknown): string {
  // The Worker's message names the specific problem — a rate-limited render, a
  // quote whose PDF is stale — so it is worth more than the generic sentence.
  if (error instanceof ApiRequestError && error.message) return error.message;
  return i18n.t('common.pdfDownloadFailed');
}

export interface PdfDownloadRecovery {
  /** Builds (or rebuilds) the PDF this download is missing or has outgrown. */
  regenerate: () => Promise<void>;
  /**
   * Refetched after `regenerate` succeeds — typically the same document with
   * `allowStale=1`, since the version that was just rendered for is now the
   * current one anyway, and re-asserting freshness a second time only adds a
   * seam for a concurrent edit to lose the race in.
   */
  retryUrl: string;
}

export interface PdfDownload {
  /**
   * Fetches `url` and saves it. `fallbackFilename` is only used when the
   * response does not name one, which the PDF routes always do.
   *
   * `recovery`, when given, answers a 404 (nothing has ever been rendered)
   * or 409 (rendered, but for an earlier version of the document) from `url`
   * by regenerating and retrying once, inside the same busy window the caller
   * already sees — a list that always offers the link does not get to also
   * always know whether today's click is the first one.
   */
  download: (url: string, fallbackFilename?: string, recovery?: PdfDownloadRecovery) => Promise<void>;
  /** True while this particular hook's download is running. */
  isDownloading: boolean;
}

export function usePdfDownload(): PdfDownload {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  /**
   * Rows disappear while their download runs — a receipt list refetches, the
   * quote editor moves off the review step — and a resolved promise must not
   * set state on a component that has gone. Set on mount rather than only
   * cleared on unmount, so StrictMode's mount/unmount/mount does not leave it
   * stuck false.
   */
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const download = useCallback(
    async (url: string, fallbackFilename?: string, recovery?: PdfDownloadRecovery) => {
      // A second click on a button that is already fetching would start a
      // second render of the same document, which is exactly the double work
      // the missing indicator used to cause.
      if (isDownloading) return;

      setIsDownloading(true);
      beginDownload();
      try {
        let file;
        try {
          file = await apiDownload(url);
        } catch (error) {
          if (!recovery || !(error instanceof ApiRequestError) || (error.status !== 404 && error.status !== 409)) {
            throw error;
          }
          await recovery.regenerate();
          file = await apiDownload(recovery.retryUrl);
        }
        saveBlob(file.blob, file.filename ?? fallbackFilename ?? t('common.pdfDefaultFilename'));
        finishDownload(null);
      } catch (error) {
        finishDownload(describeFailure(error));
      } finally {
        if (mounted.current) setIsDownloading(false);
      }
    },
    [isDownloading, t],
  );

  return { download, isDownloading };
}
